import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, ArrowLeft, Barbell, Heart, ShoppingBag, ImageSquare,
  ChartLineUp, Trophy, UploadSimple, Flame, CheckCircle, ShareNetwork, Copy,
} from "@phosphor-icons/react";
import PledgeWidget from "@/components/site/PledgeWidget";
import { identify as swsIdentify, trackConversion, getAttributionSnapshot } from "@/lib/analyticsClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// LocalStorage schema — persisted across refresh so a participant NEVER
// re-enters Step 1 after a page reload / tab close.
const STORE_KEY = "sws_challenge_signup_v2";

const readStore = () => {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeStore = (obj) => {
  try { window.localStorage.setItem(STORE_KEY, JSON.stringify(obj)); }
  catch { /* private mode — no-op */ }
};
const clearStore = () => {
  try { window.localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
};

/**
 * Three-step signup + support + confirmation stepper.
 * Step 1 → 4 required fields, submits to /api/sacrifice/signup (idempotent by email).
 * Step 2 → Donate (inline Pledge widget) and/or Shop (new tab). Continue when >=1 chosen.
 * Step 3 → Confirmation with Log My Minutes CTA + optional profile/photo enrich.
 * Used on /legacy (public) and /kms (kms_team=true auto-tagged).
 */
export default function SignupStepper({ kmsMode = false }) {
  const [step, setStep] = useState(1);
  const [signup, setSignup] = useState(null); // { id, log_token, name, email, location, moving_for_name, kms_team }
  const [supportChoice, setSupportChoice] = useState({ donation: false, merchandise: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const step3Ref = useRef(null);

  // Restore any in-progress signup on mount (surviving refresh / new tab).
  useEffect(() => {
    const stored = readStore();
    if (stored?.signup?.id) {
      setSignup(stored.signup);
      setSupportChoice(stored.supportChoice || { donation: false, merchandise: false });
      setStep(stored.step || 2);
    }
  }, []);

  // Persist on every change so refresh mid-flow doesn't lose progress.
  useEffect(() => {
    if (signup?.id) {
      writeStore({ signup, supportChoice, step });
    }
  }, [signup, supportChoice, step]);

  const onSubmitStep1 = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    const f = new FormData(e.currentTarget);
    const publicDisplay = String(f.get("public_display") || "first_last_initial");
    const publicNickname = String(f.get("public_nickname") || "").trim();
    const payload = {
      name: String(f.get("name") || "").trim(),
      email: String(f.get("email") || "").trim(),
      location: String(f.get("location") || "").trim(),
      moving_for_name: String(f.get("moving_for_name") || "").trim(),
      public_display: publicDisplay,
      public_nickname: publicDisplay === "nickname" ? publicNickname : "",
      // Privacy is fully driven by `public_display`. `first_last_initial`
      // and `nickname` are public choices; `private` is the only opt-out.
      // We mirror that into the legacy `leaderboard_optin` field so older
      // read paths that still key off it stay consistent.
      leaderboard_optin: publicDisplay === "private" ? "no" : "yes",
      kms_team: !!kmsMode,
      tier: "custom", // legacy field — kept so downstream reports still work
      attribution: getAttributionSnapshot(),
    };
    if (!payload.name || !payload.email || !payload.location || !payload.moving_for_name) {
      setError("Please fill in every field so we can lock in your entry.");
      setSubmitting(false);
      return;
    }
    if (payload.public_display === "nickname" && !payload.public_nickname) {
      setError("Please enter the nickname you want shown publicly.");
      setSubmitting(false);
      return;
    }
    try {
      const r = await fetch(`${BACKEND}/api/sacrifice/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || "Signup failed. Please try again in a moment.");
      }
      const d = await r.json();
      const nextSignup = {
        id: d.id,
        log_token: d.log_token,
        name: payload.name,
        email: payload.email,
        location: payload.location,
        moving_for_name: payload.moving_for_name,
        public_display: payload.public_display,
        public_nickname: payload.public_nickname,
        kms_team: !!d.kms_team || !!payload.kms_team,
      };
      setSignup(nextSignup);
      setStep(2);
      // Analytics — link session, record conversion
      try {
        swsIdentify(payload.email, kmsMode ? "kms_signup" : "sacrifice_signup", payload.name);
        trackConversion(kmsMode ? "kms_signup" : "sacrifice_signup", { email: payload.email, refId: d.id || "" });
      } catch { /* silent */ }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const persistChoiceServer = async (choice) => {
    if (!signup?.id) return;
    try {
      await fetch(`${BACKEND}/api/sacrifice/support-choice`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_id: signup.id, support_choice: choice }),
      });
    } catch { /* best-effort; the localStorage copy is source of truth in-session */ }
  };

  const selectedChoiceLabel = () => {
    if (supportChoice.donation && supportChoice.merchandise) return "both";
    if (supportChoice.donation) return "donation";
    if (supportChoice.merchandise) return "merchandise";
    return "";
  };

  // Toggle donation on/off. Fully reversible. The Pledge widget mount is
  // driven ONLY by supportChoice.donation to avoid split-state drift.
  const toggleDonation = () => {
    setSupportChoice((p) => {
      const nextOn = !p.donation;
      const next = { ...p, donation: nextOn };
      const choice = next.donation && next.merchandise ? "both" : next.donation ? "donation" : next.merchandise ? "merchandise" : "";
      if (choice) persistChoiceServer(choice);
      if (nextOn) {
        try { trackConversion("donation_click", { email: signup?.email || "", refId: signup?.id || "" }); } catch { /* silent */ }
        try { trackConversion("support_choice", { email: signup?.email || "", refId: choice }); } catch { /* silent */ }
      }
      return next;
    });
    // Scroll to the widget once it mounts (only when turning ON — deselecting
    // shouldn't scroll anywhere).
    setTimeout(() => {
      const el = document.getElementById("stepper-donate-inline");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // Toggle merch selection on/off. Just records INTENT — the actual shop
  // navigation only happens from Step 3 after the participant has confirmed
  // their signup is complete. This prevents anyone from leaving mid-signup
  // and thinking they finished when they hadn't.
  const toggleMerch = () => {
    setSupportChoice((p) => {
      const next = { ...p, merchandise: !p.merchandise };
      const choice = next.donation && next.merchandise ? "both" : next.donation ? "donation" : next.merchandise ? "merchandise" : "";
      if (choice) persistChoiceServer(choice);
      if (next.merchandise) {
        try { trackConversion("merch_click", { email: signup?.email || "", refId: signup?.id || "" }); } catch { /* silent */ }
      }
      try { trackConversion("support_choice", { email: signup?.email || "", refId: choice || "cleared" }); } catch { /* silent */ }
      return next;
    });
  };

  const advanceToStep3 = () => {
    setStep(3);
    setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const startOver = () => {
    clearStore();
    setSignup(null);
    setSupportChoice({ donation: false, merchandise: false });
    setStep(1);
  };

  // ---------- Render ----------
  return (
    <section
      id="legacy-signup"
      data-testid="signup-stepper"
      className="relative px-4 md:px-8 py-20 md:py-28 bg-[#B32027] text-white border-t-4 border-b-4 border-[#0A0A0A]"
    >
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
        <span className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#B32027] px-5 py-2 text-[10px] font-bold uppercase tracking-[0.3em] border-2 border-[#B32027] shadow-[0_0_30px_rgba(179,32,39,0.7)]">
          <Flame size={12} weight="fill" /> The most important step
        </span>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Step progress dots */}
        <div className="flex items-center justify-center gap-3 mb-8" data-testid="stepper-progress">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-3">
              <span
                className={`w-8 h-8 flex items-center justify-center text-xs font-bold border-2 ${
                  step === n
                    ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                    : step > n
                      ? "bg-white border-white text-[#B32027]"
                      : "bg-transparent border-white/50 text-white/70"
                }`}
                data-testid={`stepper-dot-${n}`}
              >
                {step > n ? <CheckCircle size={16} weight="fill" /> : n}
              </span>
              {n < 3 && <span className={`h-0.5 w-8 md:w-12 ${step > n ? "bg-white" : "bg-white/30"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <Step1
            kmsMode={kmsMode}
            onSubmit={onSubmitStep1}
            submitting={submitting}
            error={error}
            prefill={signup}
          />
        )}

        {step === 2 && signup?.id && (
          <Step2
            signup={signup}
            kmsMode={kmsMode}
            supportChoice={supportChoice}
            onToggleDonation={toggleDonation}
            onToggleMerch={toggleMerch}
            onContinue={advanceToStep3}
            onBackToStep1={() => setStep(1)}
            selectedChoiceLabel={selectedChoiceLabel()}
          />
        )}

        {step === 3 && signup?.id && (
          <div ref={step3Ref}>
            <Step3
              signup={signup}
              kmsMode={kmsMode}
              supportChoice={supportChoice}
              onStartOver={startOver}
              onBackToStep2={() => setStep(2)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

// -------------------- Step 1 -------------------------------------------------
function Step1({ kmsMode, onSubmit, submitting, error, prefill }) {
  // If the participant navigated Back from Step 2, their previously-entered
  // values are prefilled so they can tweak without retyping. The signup
  // endpoint is idempotent by email, so re-submitting with the same email
  // merges any edits into the existing record.
  const initialDisplay = prefill?.public_display || "first_last_initial";
  const [publicDisplay, setPublicDisplay] = useState(initialDisplay);
  // Fire once when Step 1 mounts — powers the "signup started" number in the
  // admin funnel. Guarded by a session sentinel so refreshes don't inflate it.
  useEffect(() => {
    try {
      const key = kmsMode ? "sws_signup_started_kms" : "sws_signup_started_legacy";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        trackConversion("signup_started", { refId: kmsMode ? "kms" : "legacy" });
      }
    } catch { /* private mode — no-op */ }
  }, [kmsMode]);
  return (
    <div className="border-4 border-[#0A0A0A] bg-[#B32027] p-6 md:p-10 shadow-[0_0_60px_rgba(0,0,0,0.4)]" data-testid="stepper-step-1">
      <p className="text-white/85 uppercase tracking-[0.4em] text-xs font-bold mb-4">/ Step 1 · Sign up</p>
      <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl mb-4">
        Join the 911-Minute<br/>Legacy Challenge.
      </h2>
      <p className="text-white text-lg leading-relaxed mb-2 font-medium">
        Signing up takes less than one minute.
      </p>
      <p className="text-white/90 text-base md:text-lg leading-relaxed mb-8">
        Register first, then make a donation, purchase Legacy merchandise, or do both. You&rsquo;ll receive a personal link to log and track your minutes.
        {kmsMode && <span className="block mt-3 text-white font-bold">You&rsquo;re joining through the KMS Technology roster — your entry is auto-tagged for KMS reporting.</span>}
      </p>
      <form onSubmit={onSubmit} className="space-y-4" data-testid="stepper-form-1">
        <input
          required
          name="name"
          defaultValue={prefill?.name || ""}
          placeholder="Full name"
          data-testid="stepper-name"
          className="w-full bg-white text-[#0A0A0A] px-4 py-4 placeholder-black/50 border-2 border-[#0A0A0A]"
        />
        <input
          required
          type="email"
          name="email"
          defaultValue={prefill?.email || ""}
          placeholder="Email address"
          data-testid="stepper-email"
          className="w-full bg-white text-[#0A0A0A] px-4 py-4 placeholder-black/50 border-2 border-[#0A0A0A]"
        />
        <input
          required
          name="location"
          defaultValue={prefill?.location || ""}
          placeholder="City, State or Country"
          data-testid="stepper-location"
          className="w-full bg-white text-[#0A0A0A] px-4 py-4 placeholder-black/50 border-2 border-[#0A0A0A]"
        />
        <input
          required
          name="moving_for_name"
          defaultValue={prefill?.moving_for_name || ""}
          placeholder="Who are you moving for?"
          data-testid="stepper-moving-for"
          className="w-full bg-white text-[#0A0A0A] px-4 py-4 placeholder-black/50 border-2 border-[#0A0A0A]"
        />

        {/* Public display preference — full name / email / phone stay private
            regardless of choice. Only what the participant selects here is
            surfaced on the leaderboard, Legacy Wall, community feeds, and any
            public share copy. */}
        <fieldset className="bg-[#0A0A0A] border-2 border-[#0A0A0A] p-4 space-y-3" data-testid="stepper-public-display">
          <legend className="text-[#FFC72C] text-[10px] uppercase tracking-[0.3em] font-bold px-2">
            How would you like to appear publicly?
          </legend>
          <p className="text-white/70 text-xs leading-relaxed">
            Your full last name, email, and phone are <strong className="text-white">never</strong> shown publicly. Pick how your name appears on the leaderboard, Legacy Wall, and shared posts.
          </p>
          {[
            { v: "first_last_initial", l: "Show my first name and last initial", ex: "e.g. Aaron J." },
            { v: "nickname", l: "Show a nickname", ex: "e.g. AJ, SweatKing, Team Slay" },
            { v: "private", l: "Keep my participation private", ex: "Shown as 'Anonymous' publicly. Your minutes still count toward the total." },
          ].map((opt) => (
            <label key={opt.v} className="flex items-start gap-3 cursor-pointer text-white text-sm">
              <input
                type="radio"
                name="public_display"
                value={opt.v}
                required
                checked={publicDisplay === opt.v}
                onChange={(e) => setPublicDisplay(e.target.value)}
                data-testid={`stepper-public-display-${opt.v}`}
                className="mt-1 accent-[#FFC72C]"
              />
              <span>
                <span className="font-bold">{opt.l}</span>
                <span className="block text-white/60 text-xs mt-0.5">{opt.ex}</span>
              </span>
            </label>
          ))}
          {publicDisplay === "nickname" && (
            <input
              required
              name="public_nickname"
              defaultValue={prefill?.public_nickname || ""}
              maxLength={60}
              placeholder="Your public nickname"
              data-testid="stepper-nickname"
              className="w-full bg-white text-[#0A0A0A] px-4 py-3 placeholder-black/50 border-2 border-white/30"
            />
          )}
        </fieldset>

        {error && <p data-testid="stepper-error" className="text-white bg-black/40 px-4 py-3 text-sm">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          data-testid="stepper-submit-1"
          className="w-full inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-black text-white px-8 py-5 text-sm font-bold uppercase tracking-[0.3em] disabled:opacity-50"
        >
          {submitting ? "Saving your entry…" : "Join the Challenge"} <ArrowUpRight size={14} weight="bold" />
        </button>
        <p className="text-white/80 text-xs leading-relaxed">
          By joining you agree to receive challenge emails at the address above. We keep your full last name, email and phone private — you control how you show up publicly using the setting above.
        </p>
      </form>
    </div>
  );
}

// -------------------- Step 2 -------------------------------------------------
function Step2({ signup, kmsMode, supportChoice, onToggleDonation, onToggleMerch, onContinue, onBackToStep1, selectedChoiceLabel }) {
  const canContinue = supportChoice.donation || supportChoice.merchandise;

  return (
    <div className="border-4 border-[#0A0A0A] bg-[#B32027] p-6 md:p-10 shadow-[0_0_60px_rgba(0,0,0,0.4)]" data-testid="stepper-step-2">
      <button
        type="button"
        onClick={onBackToStep1}
        data-testid="stepper-back-step1"
        className="inline-flex items-center gap-2 text-white/80 hover:text-white text-[10px] uppercase tracking-[0.3em] font-bold mb-4 underline underline-offset-4"
      >
        <ArrowLeft size={12} weight="bold" /> Back — edit my info
      </button>
      <p className="text-white/85 uppercase tracking-[0.4em] text-xs font-bold mb-4">/ Step 2 · Support the challenge</p>
      <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl mb-4">
        Choose how you&rsquo;ll<br/>support the challenge.
      </h2>
      <p className="text-white text-lg leading-relaxed mb-8 font-medium">
        Pick donate, merch, or both — then tap Continue to finish signing up. You&rsquo;ll be able to shop the merch from your confirmation page, without losing anything you&rsquo;ve entered.
      </p>
      <div className="border-2 border-[#0A0A0A] bg-[#0A0A0A]/40 p-4 mb-6 text-sm text-white/90" data-testid="stepper-registration-saved">
        <span className="text-[#FFC72C] font-bold uppercase tracking-[0.2em] text-[10px] mr-2">/ Saved</span>
        {signup.name}&rsquo;s registration is locked in — moving for <strong>{signup.moving_for_name}</strong> from {signup.location}. A confirmation email is on the way.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        {/* Donate — toggles inline Pledge widget */}
        <button
          type="button"
          onClick={onToggleDonation}
          data-testid="stepper-choose-donate"
          aria-pressed={supportChoice.donation}
          className={`p-5 border-2 text-left transition-all ${supportChoice.donation ? "border-[#FFC72C] bg-[#0A0A0A] ring-2 ring-[#FFC72C]/40" : "border-[#0A0A0A] bg-[#0A0A0A] hover:bg-black"}`}
        >
          <div className="flex items-center gap-3 mb-2">
            <Heart size={22} weight="fill" className="text-[#B32027]" />
            <span className="font-display font-black uppercase tracking-tighter text-xl md:text-2xl text-white">Make a Donation</span>
            {supportChoice.donation && <CheckCircle size={20} weight="fill" className="text-[#FFC72C] ml-auto" />}
          </div>
          <p className="text-white/70 text-sm leading-snug">Split 50/50 between Tunnel to Towers &amp; FDNY Fire Family Transport, via Pledge. Any amount.</p>
          {supportChoice.donation && (
            <p className="text-[#FFC72C] text-[10px] uppercase tracking-[0.25em] font-bold mt-2">Selected · Tap to close the widget</p>
          )}
        </button>

        {/* Merch — toggle ONLY. Records intent so the confirmation page can
            surface a big "Shop merch now" CTA. Does NOT open the shop from
            here so participants never leave the signup form mid-way. */}
        <button
          type="button"
          onClick={onToggleMerch}
          data-testid="stepper-choose-merch"
          aria-pressed={supportChoice.merchandise}
          className={`p-5 border-2 text-left transition-all ${supportChoice.merchandise ? "border-[#FFC72C] bg-[#0A0A0A] ring-2 ring-[#FFC72C]/40" : "border-[#0A0A0A] bg-[#0A0A0A] hover:bg-black"}`}
        >
          <div className="flex items-center gap-3 mb-2">
            <ShoppingBag size={22} weight="fill" className="text-[#FF1B91]" />
            <span className="font-display font-black uppercase tracking-tighter text-xl md:text-2xl text-white">Shop Legacy Merch</span>
            {supportChoice.merchandise && <CheckCircle size={20} weight="fill" className="text-[#FFC72C] ml-auto" />}
          </div>
          <p className="text-white/70 text-sm leading-snug">Every net dollar of profit is split 50/50 to the same two foundations. Wear the tribute.</p>
          <p className="text-[#FFC72C] text-[10px] uppercase tracking-[0.25em] font-bold mt-2">
            {supportChoice.merchandise ? "Selected — you'll shop from the confirmation page" : "Select this to unlock the shop after signup"}
          </p>
        </button>
      </div>
      <p className="text-white/85 text-sm text-center mb-6" data-testid="stepper-both-hint">
        You can pick one, the other, or both. Nothing is charged from here — tap Continue to finish signing up first.
      </p>

      {/* Inline Pledge widget — mounts when donation is selected */}
      {supportChoice.donation && (
        <div id="stepper-donate-inline" data-testid="stepper-donate-inline" className="border-4 border-[#0A0A0A] bg-white p-3 md:p-4 mb-6">
          <div className="px-2 pt-2 pb-3 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[#B32027] uppercase tracking-[0.3em] text-[10px] font-bold mb-1">/ Complete your donation</p>
              <p className="text-[#0A0A0A] text-sm md:text-base leading-snug">
                Every direct donated dollar is divided equally between <strong>Tunnel to Towers Foundation</strong> and <strong>FDNY Fire Family Transport Foundation</strong>. Sweat With Slay never holds the funds.
              </p>
            </div>
            <button
              type="button"
              onClick={onToggleDonation}
              data-testid="stepper-close-donate"
              className="text-[#B32027] hover:text-black text-[10px] uppercase tracking-[0.25em] font-bold underline underline-offset-2"
            >
              Cancel donation
            </button>
          </div>
          <div className="min-h-[620px]">
            <PledgeWidget />
          </div>
          <div className="border-t-2 border-[#B32027]/20 mt-2 pt-4 pb-2 px-2 text-center">
            <p className="text-[#0A0A0A]/70 text-sm mb-3">Donation form not loading?</p>
            <a
              href="https://pledge.to/campaigns/47749/sweat-with-slay"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="stepper-donate-fallback-btn"
              className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em]"
            >
              Open the Secure Donation Page <ArrowUpRight size={12} weight="bold" />
            </a>
          </div>
        </div>
      )}

      {/* Continue CTA — prominent once unlocked */}
      {canContinue && (
        <div className="relative mt-2" data-testid="stepper-continue-wrap">
          <span aria-hidden className="absolute -inset-1 bg-[#FFC72C] blur-md opacity-70 animate-pulse" />
          <button
            type="button"
            onClick={onContinue}
            data-testid="stepper-continue"
            className="relative w-full inline-flex items-center justify-center gap-4 bg-[#FFC72C] hover:bg-yellow-300 text-[#0A0A0A] px-8 py-7 md:py-8 text-base md:text-xl font-black uppercase tracking-[0.28em] border-4 border-[#0A0A0A] shadow-[0_0_40px_rgba(255,199,44,0.7)] hover:shadow-[0_0_60px_rgba(255,199,44,0.9)] transition-all"
          >
            <span>Continue to My Challenge</span>
            <ArrowRight size={22} weight="bold" className="animate-[bounce_1.4s_infinite]" />
          </button>
          <p className="text-white text-xs md:text-sm mt-4 text-center font-medium" data-testid="stepper-continue-hint">
            Almost done — tap the button above to finish signing up. Your merch shop opens from the next page.
          </p>
          <p className="text-white/75 text-xs mt-1 text-center" data-testid="stepper-selected-label">
            Support locked in: <strong>{selectedChoiceLabel}</strong>.
          </p>
        </div>
      )}
      {!canContinue && (
        <>
          <button
            type="button"
            onClick={onContinue}
            disabled
            data-testid="stepper-continue"
            className="w-full inline-flex items-center justify-center gap-3 bg-[#0A0A0A]/40 text-white/50 px-8 py-5 text-sm font-bold uppercase tracking-[0.3em] cursor-not-allowed"
          >
            Continue to My Challenge <ArrowRight size={14} weight="bold" />
          </button>
          <p className="text-white/80 text-xs mt-3 text-center">
            Pick donate, merch, or both above to unlock your challenge dashboard.
          </p>
        </>
      )}
    </div>
  );
}

// -------------------- Step 3 -------------------------------------------------
function Step3({ signup, kmsMode, supportChoice, onStartOver, onBackToStep2 }) {
  const first = (signup.name || "").split(/\s+/)[0] || "friend";
  const logUrl = signup.log_token ? `/log?t=${signup.log_token}` : "/log";
  return (
    <div className="border-4 border-[#0A0A0A] bg-gradient-to-br from-[#0A0A0A] via-[#0F0F0F] to-[#B32027]/25 p-6 md:p-10 text-white" data-testid="stepper-step-3">
      <button
        type="button"
        onClick={onBackToStep2}
        data-testid="stepper-back-step2"
        className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[10px] uppercase tracking-[0.3em] font-bold mb-4 underline underline-offset-4"
      >
        <ArrowLeft size={12} weight="bold" /> Back — change my support choice
      </button>
      <p className="text-[#FFC72C] uppercase tracking-[0.35em] text-[10px] font-bold mb-3">/ Your signup is complete</p>
      <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl mb-4">
        {first}&rsquo;s Sweat for<br/>Sacrifice Challenge.
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 text-sm" data-testid="stepper-summary">
        <div className="border border-white/20 bg-black/30 p-4">
          <p className="text-white/50 uppercase tracking-[0.2em] text-[10px] font-bold mb-1">Moving for</p>
          <p className="text-white font-bold text-base leading-snug">{signup.moving_for_name}</p>
        </div>
        <div className="border border-white/20 bg-black/30 p-4">
          <p className="text-white/50 uppercase tracking-[0.2em] text-[10px] font-bold mb-1">From</p>
          <p className="text-white font-bold text-base leading-snug">{signup.location}</p>
        </div>
        <div className="border border-white/20 bg-black/30 p-4">
          <p className="text-white/50 uppercase tracking-[0.2em] text-[10px] font-bold mb-1">Progress</p>
          <p className="text-white font-bold text-base leading-snug">0 of 911 minutes completed</p>
        </div>
      </div>

      {/* Big "You're done — now shop" CTA only if they selected merch on Step 2.
          Sits ABOVE the log-my-minutes button because merch is time-sensitive
          (they said they wanted it, so surface it prominently now that signup
          is confirmed complete). */}
      {supportChoice.merchandise && (
        <a
          href={`${kmsMode ? "/kms/shop" : "/shop"}?from=signup&email=${encodeURIComponent(signup.email || "")}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="stepper-step3-shop-primary"
          className="w-full inline-flex items-center justify-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-8 py-6 text-base font-bold uppercase tracking-[0.28em] mb-3 shadow-[0_0_35px_rgba(255,27,145,0.55)]"
        >
          <ShoppingBag size={20} weight="fill" /> Signup Complete — Shop the Merch Now <ArrowUpRight size={16} weight="bold" />
        </a>
      )}

      {/* Primary action — most prominent */}
      <Link
        to={logUrl}
        data-testid="stepper-step3-log"
        className="w-full inline-flex items-center justify-center gap-3 bg-[#B32027] hover:bg-[#D42730] text-white px-8 py-6 text-base font-bold uppercase tracking-[0.3em] mb-4 shadow-[0_0_30px_rgba(179,32,39,0.5)]"
      >
        <Barbell size={20} weight="fill" /> Log My Minutes <ArrowRight size={16} weight="bold" />
      </Link>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="stepper-step3-actions">
        <Link
          to={logUrl}
          data-testid="stepper-step3-progress"
          className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-white bg-black/40 text-white px-5 py-4 text-[11px] font-bold uppercase tracking-[0.25em]"
        >
          <ChartLineUp size={16} weight="bold" /> View My Progress
        </Link>
        <button
          type="button"
          onClick={() => document.getElementById("stepper-share-anchor")?.scrollIntoView({ behavior: "smooth" })}
          data-testid="stepper-step3-share"
          className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-[#FFC72C] hover:text-[#FFC72C] bg-black/40 text-white px-5 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-left"
        >
          <ShareNetwork size={16} weight="fill" /> Share the Challenge
        </button>
        <a
          href="https://pledge.to/campaigns/47749/sweat-with-slay"
          target="_blank"
          rel="noreferrer noopener"
          onClick={() => {
            try { trackConversion("donation_click", { email: signup?.email || "", refId: "step3" }); }
            catch { /* silent */ }
          }}
          data-testid="stepper-step3-donate"
          className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-[#B32027] hover:text-[#B32027] bg-black/40 text-white px-5 py-4 text-[11px] font-bold uppercase tracking-[0.25em]"
        >
          <Heart size={16} weight="fill" /> Make a Donation <ArrowUpRight size={14} weight="bold" />
        </a>
        <Link
          to={kmsMode ? "/kms/shop" : "/shop"}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="stepper-step3-shop"
          className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-[#FF1B91] hover:text-[#FF1B91] bg-black/40 text-white px-5 py-4 text-[11px] font-bold uppercase tracking-[0.25em]"
        >
          <ShoppingBag size={16} weight="fill" /> Shop Legacy Merch
        </Link>
        <a
          href="#add-story"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("stepper-add-story-anchor")?.scrollIntoView({ behavior: "smooth" });
          }}
          data-testid="stepper-step3-story"
          className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-[#FFC72C] hover:text-[#FFC72C] bg-black/40 text-white px-5 py-4 text-[11px] font-bold uppercase tracking-[0.25em] sm:col-span-2"
        >
          <ImageSquare size={16} weight="fill" /> Add My Legacy Story
        </a>
      </div>

      <div id="stepper-share-anchor" />
      <ShareChallengeBlock signup={signup} />

      <div id="stepper-add-story-anchor" />
      <OptionalProfileEnrich signup={signup} />

      <div className="mt-8 flex items-center justify-between flex-wrap gap-3 border-t border-white/20 pt-6">
        <p className="text-white/60 text-xs leading-relaxed">
          Bookmark <a href={logUrl} className="text-[#FFC72C] underline font-bold">{typeof window !== "undefined" ? window.location.origin.replace(/^https?:\/\//, "") : "sweatwithslay.com"}{logUrl}</a> — your personal logging link. Same on desktop and mobile.
        </p>
        <button
          type="button"
          onClick={onStartOver}
          data-testid="stepper-start-over"
          className="text-white/50 hover:text-white text-[10px] uppercase tracking-[0.3em] font-bold underline"
        >
          Sign up someone else
        </button>
      </div>
    </div>
  );
}

// -------------------- Share the Challenge block (Step 3) --------------------
function ShareChallengeBlock({ signup }) {
  const [copied, setCopied] = useState(false);
  // Public participants get their dedication in the copy; private
  // participants get the anonymous version. Mirrors the backend's
  // is_public() rule.
  const isPublic = signup?.public_display && signup.public_display !== "private";
  const dedication = signup?.moving_for_name || "";
  const challengeUrl = "https://sweatwithslay.com/legacy";
  const shareText = (isPublic && dedication)
    ? `I joined the Sweat for Sacrifice Challenge and committed to moving 911 minutes this September in honor of ${dedication}. Join me, donate or support the challenge through Legacy merchandise: ${challengeUrl}`
    : `I joined the Sweat for Sacrifice Challenge and committed to moving 911 minutes this September. Join me, donate or support the challenge through Legacy merchandise: ${challengeUrl}`;

  const fireShareEvent = (channel) => {
    try { trackConversion("share_click", { email: signup?.email || "", refId: channel }); }
    catch { /* silent */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      fireShareEvent("clipboard");
      setTimeout(() => setCopied(false), 3000);
    } catch { /* ignore */ }
  };

  const linkOut = (url, channel) => (e) => {
    fireShareEvent(channel);
    // Let the browser handle the actual navigation via the anchor href.
  };

  const enc = encodeURIComponent;
  return (
    <div className="border border-white/20 bg-black/30 p-5 md:p-6 mb-6" data-testid="stepper-share-block">
      <p className="text-[#FFC72C] uppercase tracking-[0.35em] text-[10px] font-bold mb-3">/ Bring someone with you (optional)</p>
      <p className="text-white/85 text-sm md:text-base leading-relaxed mb-3">
        Copy the note below and post it wherever your people are. {isPublic ? "Your dedication is included." : "Your dedication is kept private in this note."}
      </p>
      <div className="bg-[#0A0A0A] border-l-4 border-[#FFC72C] p-4 text-white text-sm leading-relaxed mb-4 whitespace-pre-line" data-testid="stepper-share-text">
        {shareText}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={copy} data-testid="stepper-share-copy" className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-yellow-300 text-[#0A0A0A] px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em]">
          <Copy size={14} weight="bold" /> {copied ? "Copied!" : "Copy note"}
        </button>
        <a
          href={`https://twitter.com/intent/tweet?text=${enc(shareText)}`}
          target="_blank"
          rel="noreferrer noopener"
          onClick={linkOut(null, "twitter")}
          data-testid="stepper-share-x"
          className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em]"
        >
          Post on X
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(challengeUrl)}`}
          target="_blank"
          rel="noreferrer noopener"
          onClick={linkOut(null, "linkedin")}
          data-testid="stepper-share-linkedin"
          className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em]"
        >
          LinkedIn
        </a>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${enc(challengeUrl)}&quote=${enc(shareText)}`}
          target="_blank"
          rel="noreferrer noopener"
          onClick={linkOut(null, "facebook")}
          data-testid="stepper-share-facebook"
          className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em]"
        >
          Facebook
        </a>
        <a
          href={`sms:?&body=${enc(shareText)}`}
          onClick={linkOut(null, "sms")}
          data-testid="stepper-share-sms"
          className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em]"
        >
          Text a friend
        </a>
        <a
          href={`mailto:?subject=${enc("Sweat for Sacrifice — 911 minutes")}&body=${enc(shareText)}`}
          onClick={linkOut(null, "email")}
          data-testid="stepper-share-email"
          className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em]"
        >
          Email
        </a>
      </div>
    </div>
  );
}

// -------------------- Optional profile enrich (Step 3) -----------------------
function OptionalProfileEnrich({ signup }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState("");
  const [photos, setPhotos] = useState([]);
  const [primaryIdx, setPrimaryIdx] = useState(0);
  const [uploading, setUploading] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg("");
    const f = new FormData(e.currentTarget);
    const payload = {
      name: signup.name,
      email: signup.email, // idempotency key
      phone: String(f.get("phone") || "").trim(),
      moving_for_name: signup.moving_for_name,
      moving_for_relationship: String(f.get("moving_for_relationship") || "").trim(),
      story: String(f.get("story") || "").trim(),
      sharing_consent: String(f.get("sharing_consent") || "private"),
      leaderboard_optin: String(f.get("leaderboard_optin") || "no"),
      movement_plan: String(f.get("movement_plan") || "").trim(),
      hear_about: String(f.get("hear_about") || "").trim(),
      kms_team: !!signup.kms_team,
    };
    try {
      const r = await fetch(`${BACKEND}/api/sacrifice/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("Save failed");
      setSaved(true);
      setMsg("Saved. Thanks for the extra context — it helps us tell the story.");
      try { trackConversion("wall_completed", { email: signup?.email || "" }); } catch { /* silent */ }
    } catch (err) {
      setMsg(err.message || "Could not save right now. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhotos = async () => {
    if (!photos.length || !signup?.email) return;
    setUploading(true); setMsg("");
    try {
      for (let i = 0; i < photos.length; i++) {
        const fd = new FormData();
        fd.append("email", signup.email);
        fd.append("file", photos[i]);
        fd.append("caption", "");
        fd.append("primary", i === primaryIdx ? "true" : "false");
        await fetch(`${BACKEND}/api/sacrifice/photo`, { method: "POST", body: fd });
      }
      setMsg(`${photos.length} photo${photos.length === 1 ? "" : "s"} uploaded — we review each one before it goes live on the Wall.`);
      setPhotos([]);
    } catch (err) {
      setMsg(err.message || "Photo upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <details className="border border-white/20 bg-black/30 p-5" data-testid="stepper-optional-profile"
      onToggle={(e) => {
        if (e.currentTarget.open) {
          try { trackConversion("wall_started", { email: signup?.email || "" }); } catch { /* silent */ }
        }
      }}>
      <summary className="cursor-pointer list-none">
        <span className="text-[#FFC72C] uppercase tracking-[0.3em] text-[10px] font-bold">/ Optional — add your legacy story (recommended)</span>
        <p className="text-white/90 text-sm mt-2">Tap to add a photo, story, or details about how you plan to move. All optional — takes 2 minutes.</p>
      </summary>
      <div className="mt-5 space-y-4">
        <div>
          <p className="text-white/60 text-[10px] uppercase tracking-[0.25em] font-bold mb-2">Photos for the Legacy Wall (up to 5)</p>
          <input
            type="file"
            accept="image/*"
            multiple
            data-testid="stepper-photo-input"
            onChange={(e) => {
              const files = Array.from(e.target.files || []).slice(0, 5);
              setPhotos(files);
              setPrimaryIdx(0);
            }}
            className="w-full bg-white text-[#0A0A0A] file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0A0A0A] file:text-white file:font-bold file:uppercase file:text-xs file:tracking-[0.2em] px-2 py-2 border-2 border-white/30"
          />
          {photos.length > 0 && (
            <>
              <div className="flex gap-3 flex-wrap mt-3">
                {photos.map((f, i) => (
                  <button
                    key={`${f.name}-${i}`}
                    type="button"
                    onClick={() => setPrimaryIdx(i)}
                    className={`w-20 h-20 border-2 overflow-hidden ${primaryIdx === i ? "border-[#FFC72C] ring-2 ring-[#FFC72C]" : "border-white/30"}`}
                  >
                    <img src={URL.createObjectURL(f)} alt={`preview ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={uploadPhotos}
                disabled={uploading}
                data-testid="stepper-photo-upload"
                className="mt-3 inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-yellow-400 text-[#0A0A0A] px-5 py-3 text-[11px] font-bold uppercase tracking-[0.25em] disabled:opacity-50"
              >
                <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : "Upload photos"}
              </button>
            </>
          )}
        </div>
        <form onSubmit={saveProfile} className="space-y-3">
          <input name="moving_for_relationship" placeholder="Their relationship to you (optional)" className="w-full bg-white text-[#0A0A0A] px-4 py-3 placeholder-black/50 border-2 border-white/30" />
          <textarea name="story" rows={4} maxLength={3000} placeholder="Share a short story about them (optional)" className="w-full bg-white text-[#0A0A0A] px-4 py-3 placeholder-black/50 border-2 border-white/30" />
          <input name="phone" placeholder="Phone (optional)" className="w-full bg-white text-[#0A0A0A] px-4 py-3 placeholder-black/50 border-2 border-white/30" />
          <select name="movement_plan" defaultValue="" className="w-full bg-white text-[#0A0A0A] px-4 py-3 border-2 border-white/30">
            <option value="" disabled>How do you plan to move? (optional)</option>
            <option value="walking">Walking</option>
            <option value="gym">Gym workouts</option>
            <option value="running">Running</option>
            <option value="cycling">Cycling</option>
            <option value="yoga">Yoga / stretching</option>
            <option value="sports">Sports</option>
            <option value="mix">A mix of everything</option>
            <option value="unsure">Not sure yet</option>
          </select>
          <div>
            <p className="text-white/60 text-[10px] uppercase tracking-[0.25em] font-bold mb-2">Can we share your story publicly?</p>
            <div className="flex flex-col gap-1 text-sm">
              {[
                { v: "full", l: "Yes — name, photo, and story" },
                { v: "name_only", l: "Yes — name only" },
                { v: "private", l: "Keep it private (default)" },
              ].map((o) => (
                <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="sharing_consent" value={o.v} defaultChecked={o.v === "private"} className="accent-[#FFC72C]" />
                  <span>{o.l}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-white/60 text-[10px] uppercase tracking-[0.25em] font-bold mb-2">Would you like to appear on the public leaderboard?</p>
            {signup.public_display === "private" ? (
              <p className="text-white/70 text-xs leading-relaxed bg-[#0A0A0A] border border-white/20 p-3" data-testid="stepper-leaderboard-locked">
                You picked <strong>Keep my participation private</strong> on Step 1, so you&rsquo;re automatically off the public leaderboard. Your minutes still count toward the overall 911 total.
              </p>
            ) : (
              <div className="flex flex-col gap-1 text-sm">
                {[
                  {
                    v: "yes",
                    l: signup.public_display === "nickname"
                      ? `Yes — list me as "${signup.public_nickname || "your nickname"}"`
                      : "Yes — list me (first name + last initial)",
                  },
                  { v: "no", l: "No — keep my minutes private (still counts toward the total)" },
                ].map((o) => (
                  <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="leaderboard_optin"
                      value={o.v}
                      defaultChecked={o.v === "no"}
                      data-testid={`stepper-leaderboard-${o.v}`}
                      className="accent-[#FFC72C]"
                    />
                    <span>{o.l}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <select name="hear_about" defaultValue="" className="w-full bg-white text-[#0A0A0A] px-4 py-3 border-2 border-white/30">
            <option value="" disabled>How did you hear about this? (optional)</option>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="linkedin">LinkedIn</option>
            <option value="facebook">Facebook</option>
            <option value="kms">KMS Technology</option>
            <option value="word_of_mouth">Word of mouth / a friend</option>
            <option value="existing_client">I train with Sweat With Slay already</option>
            <option value="family">Family</option>
            <option value="other">Other</option>
          </select>
          <button
            type="submit"
            disabled={saving}
            data-testid="stepper-save-profile"
            className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] hover:bg-white/90 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] disabled:opacity-50"
          >
            {saving ? "Saving…" : saved ? "Update details" : "Save my details"} <ArrowRight size={12} weight="bold" />
          </button>
          {msg && <p data-testid="stepper-profile-msg" className="text-[#FFC72C] text-sm mt-2">{msg}</p>}
        </form>
      </div>
    </details>
  );
}
